import { Component, OnInit, OnDestroy } from '@angular/core';
import { NotificationService } from '../../../modules/association/services/notification.service';
import { SignalRNotificationService } from '../../../shared/services/signalr-notification.service';
import { NotificationDto, NotificationListDto } from '../../../modules/association/models/notification.models';
import { BaseComponent } from '../component-base';

@Component({
  selector: 'app-notification-icon',
  templateUrl: './notification-icon.component.html',
  styleUrls: ['./notification-icon.component.scss']
})
export class NotificationIconComponent extends BaseComponent implements OnInit, OnDestroy {
  unreadCount: number = 0;
  notifications: NotificationDto[] = [];
  isLoading: boolean = false;
  isDropdownOpen: boolean = false;
  currentPage: number = 1;
  pageSize: number = 10;
  totalItems: number = 0;
  currentUserId: string = '';

  constructor(
    private notificationService: NotificationService,
    private signalRService: SignalRNotificationService
  ) {
    super();
    this.currentUserId = this.getUserId;
  }

  ngOnInit(): void {
    this.loadUnreadCount();
    this.setupSignalRListener();
  }

  ngOnDestroy(): void {
    this.signalRService.leaveUserGroup(this.currentUserId);
  }

  private setupSignalRListener(): void {
    this.signalRService.startConnection()
      .then(() => {
        if (this.signalRService.isConnected) {
          return this.signalRService.joinUserGroup(this.currentUserId);
        }
        return Promise.resolve();
      })
      .then(() => {
        if (this.signalRService.isConnected) {
          console.log('Joined notification user group');
        } else {
          console.log('SignalR not available - real-time notifications disabled');
        }
      })
      .catch(err => {
        // Silently fail - app will work without real-time notifications
        console.warn('SignalR not available:', err.message);
      });

    this.signalRService.onNotificationReceived$.subscribe(notification => {
      if (notification && notification.recipientUserId === this.currentUserId) {
        this.unreadCount++;
        this.notifications.unshift(notification);
      }
    });
  }

  loadUnreadCount(): void {
    this.notificationService.getUnreadCount().subscribe({
      next: (res) => {
        console.log('Unread count response:', res);
        if (res.success) {
          this.unreadCount = res.value;
          console.log('Unread count loaded:', this.unreadCount);
        } else {
          console.error('Failed to load unread count:', res.message);
        }
      },
      error: (err) => {
        console.error('Error loading unread count:', err);
      }
    });
  }

  toggleDropdown(): void {
    if (!this.isDropdownOpen) {
      this.openDropdown();
    } else {
      this.isDropdownOpen = false;
    }
  }

  loadNotifications(pageNumber: number = 1): void {
    this.isLoading = true;
    this.currentPage = pageNumber;

    this.notificationService.getNotifications(pageNumber, this.pageSize).subscribe({
      next: (res) => {
        console.log('Notifications response:', res);
        if (res.success) {
          const data = res.value as NotificationListDto;
          this.notifications = data.items;
          this.totalItems = data.totalItemsCount;
          console.log('Notifications loaded:', this.notifications.length, 'Total:', this.totalItems);
        } else {
          console.error('Failed to load notifications:', res.message);
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading notifications:', err);
        this.isLoading = false;
      }
    });
  }

  openDropdown(): void {
    this.isDropdownOpen = true;
    // Reset to page 1 to show most recent notifications
    this.currentPage = 1;
    this.loadNotifications(1);
  }

  loadMore(): void {
    if (this.notifications.length < this.totalItems) {
      this.loadNotifications(this.currentPage + 1);
    }
  }

  onNotificationClick(notificationId: string): void {
    this.notificationService.markAsRead(notificationId).subscribe({
      next: (res) => {
        if (res.success) {
          const notification = this.notifications.find(n => n.id === notificationId);
          if (notification && !notification.isRead) {
            notification.isRead = true;
            this.unreadCount--;
          }
        }
      }
    });
    this.isDropdownOpen = false;
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead().subscribe({
      next: (res) => {
        if (res.success) {
          this.notifications.forEach(n => n.isRead = true);
          this.unreadCount = 0;
        }
      }
    });
  }

  hasMoreNotifications(): boolean {
    return this.notifications.length < this.totalItems;
  }
}
